import type { ReactNode } from 'react';
import {
  Box,
  Card,
  Chip,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import type { OfferedPlan } from '@/api/types';
import { planHighlights, priceParts, RECOMMENDED, type Interval } from './planCopy';

export function IntervalToggle({
  value,
  onChange,
}: {
  value: Interval;
  onChange: (value: Interval) => void;
}) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, v: Interval | null) => v && onChange(v)}
      aria-label="Forma de pagamento"
    >
      <ToggleButton value="month" sx={{ px: 2 }}>
        Mensal
      </ToggleButton>
      <ToggleButton value="year" sx={{ px: 2 }}>
        Anual
        <Chip size="small" color="success" variant="soft" label="2 meses grátis" sx={{ ml: 1 }} />
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

/** Up to 3 plans side by side; more wrap on medium screens and share a row on wide ones. */
function columns(count: number) {
  const n = Math.max(1, count);
  return n <= 3 ? { xs: 12, md: 12 / n } : { xs: 12, sm: 6, md: 4, xl: 12 / n };
}

/** Base plans side by side; the caller decides what each card's button does. */
export function PlanPicker({
  plans,
  interval,
  current,
  action,
}: {
  plans: OfferedPlan[];
  interval: Interval;
  current?: string | null;
  action: (plan: OfferedPlan) => ReactNode;
}) {
  const base = plans.filter((p) => p.kind === 'base');
  return (
    <Grid container spacing={2.5} sx={{ alignItems: 'stretch' }}>
      {base.map((plan) => {
        const price = priceParts(plan, interval);
        const highlight = plan.key === RECOMMENDED;
        const mine = current === plan.key;
        return (
          <Grid key={plan.key} size={columns(base.length)}>
            <Card
              sx={(theme) => ({
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                ...(highlight && {
                  borderColor: theme.vars.palette.primary.main,
                  borderWidth: 2,
                  boxShadow: `0 30px 60px -40px ${theme.alpha(theme.vars.palette.primary.main, 0.7)}`,
                }),
              })}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', justifyContent: 'space-between', minHeight: 32 }}
              >
                <Typography variant="h6" component="h3">
                  {plan.name}
                </Typography>
                {mine ? (
                  <Chip size="small" color="primary" label="Seu plano" />
                ) : highlight ? (
                  <Chip size="small" variant="soft" color="primary" label="Mais escolhido" />
                ) : null}
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'baseline', mt: 1.5 }}>
                <Typography
                  component="p"
                  sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' }}
                >
                  {price.main}
                </Typography>
                <Typography color="text.secondary">{price.unit}</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ minHeight: 20 }}>
                {price.note}
              </Typography>
              {plan.description && (
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  {plan.description}
                </Typography>
              )}
              <Stack spacing={1} sx={{ mt: 2, flex: 1 }}>
                {planHighlights(plan).map((item) => (
                  <Stack key={item} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <CheckRounded sx={{ fontSize: 20, mt: 0.1, color: 'primary.main' }} />
                    <Typography variant="body2">{item}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Box sx={{ mt: 2.5 }}>{action(plan)}</Box>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
